import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import React, { FC, useCallback } from "react";

import { stringToHash } from "../../utils/hash";
import { getSearchParams, getUpdatedSearchParamsUrl } from "../../utils/search-params";
import { WhatsNextCard } from "../cards/whats-next-card";
import { WhatsNextCardSkeleton } from "../cards/whats-next-card/whats-next-card-skeleton";
import { PaginationNumbers } from "../pagination/pagination-numbers";

export interface WhatsNextGridProps {
  whatsNextItems: Queries.WhatsNextDataFragment[];
  labels: {
    "whats-next-grid-empty-state": string;
  };
  pageSize?: number;
  pageSearchParam?: string;
  loading: boolean;
}

export const WhatsNextGrid: FC<WhatsNextGridProps> = ({
  whatsNextItems,
  labels,
  pageSize = 25,
  pageSearchParam = "whatsnext-page",
  loading,
}) => {
  const hashKey = stringToHash(whatsNextItems.map((e) => e.id).join(""));
  const searchParams = getSearchParams();
  const page = parseInt(searchParams?.get(pageSearchParam) ?? "0");
  const currentPage = whatsNextItems.slice(page * pageSize, (page + 1) * pageSize);

  const totalPages = Math.ceil((whatsNextItems.length ?? 0) / pageSize);
  const onSetCurrentPageIndex = useCallback((i: number) => {
    if (typeof window !== "undefined") {
      window.location.href = getUpdatedSearchParamsUrl({ "whatsnext-page": i });
    }
  }, []);

  return (
    <AnimatePresence initial={false} mode="popLayout">
      {loading ? (
        <div
          className={clsx(
            "grid gap-8 justify-center",
            "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5"
          )}
        >
          {new Array(currentPage.length).fill(null).map((_, i) => (
            <WhatsNextCardSkeleton key={i} />
          ))}
        </div>
      ) : currentPage.length ? (
        <motion.div
          className="h-full flex w-full flex-col justify-stretch"
          key={hashKey}
          initial={{
            opacity: 0,
          }}
          animate={{
            opacity: 1,
          }}
          exit={{
            opacity: 0,
          }}
          transition={{
            opacity: { ease: "easeOut", duration: 0.5 },
          }}
        >
          <>
            <div
              className={clsx(
                "grid gap-8 justify-center",
                "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5"
              )}
            >
              {currentPage.filter(Boolean).map((whatsNextItems, i) => (
                <WhatsNextCard {...whatsNextItems} key={i} />
              ))}
            </div>
            {currentPage.length !== whatsNextItems.length && (
              <div className="w-full flex justify-center pt-4">
                <PaginationNumbers
                  currentPageIndex={page}
                  getItemHref={(i) => getUpdatedSearchParamsUrl({ "whatsnext-page": i })}
                  setCurrentPageIndex={onSetCurrentPageIndex}
                  totalPages={totalPages}
                />
              </div>
            )}
          </>
        </motion.div>
      ) : (
        <div className="container mx-auto h-64 flex items-center justify-center">
          <p>{labels["whats-next-grid-empty-state"]}</p>
        </div>
      )}
    </AnimatePresence>
  );
};
